import moment from 'moment';
import studentInformationService from '../../../services/StudentInformationService';
import {getScreeningWindowDetails} from "../../../services/screeningWindowService";
import SRS from '../../../services/StudentRecordService'
import { getBatchAssignments } from '../../../services/assignment/getBatchAssignments';
import { getActivitiesWithStudentInfo, getFormatedId } from '../../../services/API';
import { getDefaultTemplates } from '../../../services/assignment/getAssignmentTemplate';
import manifestHelper from '../../../services/assignment/manifest'
import { ENTITY_TYPE } from '../../../services/assignment/constants';
import { removeCompletedDistrictAssignments } from '../../../services/assignment/getAssignment/getAssignment';
import { getScreeningWindows } from '../../../services/assignment/getScreeningWindows';
import { getUserData } from '../../../services/API';

/*
  Fetches tracking activities.
*/

function getFormattedId(id) {
  return String(id).replace(/\-/g, '').toUpperCase();
}

export const getReport = async (schoolId, students, screeningWindow, districtId) => {
  try {
    const window = screeningWindow.id
    const {startDate, endDate} = getScreeningWindowDetails(window);
    const dateStart = formatDate(startDate)
    const dateEnd = formatDate(endDate)

    const startOfWeek = moment().startOf('isoWeek')
    const weekStart = startOfWeek.format('YYYY-MM-DD')
    const weekEnd = moment(moment().format('YYYY-MM-DD')).add(1, 'day').format('YYYY-MM-DD')

    const studentIds = students.map(s => s.id)

    const defaultEntities = [
      { entityType: "SCHOOL", entityId: schoolId},
      { entityType: "DISTRICT", entityId: districtId},
      { entityType: "CLASS", entityId: 1234}  //Placeholder to ensure optimized assignment call
    ];

    // generate concurrent requests array
    const requests = [SRS.getStudentMetrics(studentIds, weekStart, weekEnd)]
    requests.push(getDefaultTemplates(schoolId,ENTITY_TYPE.SCHOOL,defaultEntities));
    requests.push(getScreeningWindows({schoolId, dateStart, dateEnd, defaultEntities}))

    const BATCH_SIZE = 25;
    for (let i = 0; i < studentIds.length; i += BATCH_SIZE) {
      const entityIdList = studentIds.slice(i, i+BATCH_SIZE)
      requests.push(getFilteredStudentAssignments(entityIdList, dateStart, dateEnd, districtId, schoolId))
    }

    // concurrently...
    // 1. fetch timeRead and activityCount metrics
    // 2. fetch templates
    // 3. fetch screening windows for the current testing window
    // 4. fetch assignments batched by 25 studentIds
    const response = await Promise.all(requests)
    const metrics = response[0]
    const templates = response[1];
    const windows = response[2]
    console.log("TEMPLATES",templates)
    console.log('WINDOWS', windows)

    const classAssignmentData = []
    for (let i = 3; i < response.length; i++) {
      classAssignmentData.push(...response[i])
    }

    // check for active screening window
    const activeWindow = windows.find(isActiveDistrictAssignment)
    if (activeWindow) activeWindow.window = window;

    // check for a future screening window
    const hasFutureWindow = windows.some(w => {
      const [date, time] = w.dateFrom.split(' ')
      const dateFrom = new Date(`${date}T${time}`)
      return dateFrom > new Date()
    })

    const reportData = []

    for (let i = 0; i < classAssignmentData.length; i++) {
      const {first_name, last_name, id: studentId, metadata, grade} = students[i]
      const studentGrade = grade > 6 ? 6 : grade;
      const {assignments} = classAssignmentData[i]
      const filtered = removeCompletedDistrictAssignments(assignments,studentGrade)
      const assignment = getMostRecentAssignment(filtered)
      if (assignment) assignment.status = manifestHelper.getStatus(assignment);
      const dateCreated = getCreationDate(assignment)
      const dateCompleted = getCompletedDate(assignment)
      const metric = metrics[i]
      const storyCount = metric.activityCount.total
      const incompleteStoryCount = metric.incompleteActivityCount.total
      const timeRead = metric.timeRead.sum
      const sessionTime = metric.sessionTime.sum
      const practiceMinutes = Math.round(timeRead / (1000*60))
      const tutoringMinutes = Math.round(sessionTime / (1000*60))
      const template = templates[studentGrade];
      const manifest = manifestHelper.cleanManifest(template.manifest);

      const studentData = {
        studentId,
        first_name,
        last_name,
        metadata,
        ...assignment,
        practiceMinutes,
        tutoringMinutes,
        storyCount,
        incompleteStoryCount,
        template,
        manifest,
        grade: studentGrade,
        activeWindow,
        dateCompleted,
        dateCreated,
        hasFutureWindow,
      }

      if (!studentData.status) {
        studentData.status = filtered.some(isActiveDistrictAssignment) ? 'DISTRICT_ASSIGNED' : 'UNASSESSED'
      }

      reportData.push(studentData)
    }

    // sorted by status priority
    return reportData.sort((a, b) => statusPriority[a.status] - statusPriority[b.status])
  }
  catch (e) {
    console.log(e)
  }
}

// 1 = highest priority
const statusPriority = {
  REASSESS: 1,
  'PRE-READER': 2,
  INCOMPLETE: 3,
  IN_PROGRESS: 4,
  COMPLETE: 5,
  UNDER_REVIEW: 6,
  DONE: 7,
  ASSIGNED: 8,
  DISTRICT_ASSIGNED: 9,
  UNASSESSED: 10,
}

async function getFilteredStudentAssignments(entityIdList, dateStart, dateEnd, districtId, schoolId){
  const statusesToFilter = ["ERROR", "COMPLETE", "DONE", "UNASSIGNED"];
  //let studentAssignments = [];
  let studentAssignments = await getBatchAssignments({ entityIdList, dateStart, dateEnd, districtId, schoolId, assignmentType: ["BENCHMARK","PROGRESS_MONITORING"] });

  if (getUserData().branding === 'nwea-map-tutor') {
    const formattedEntityIdList = entityIdList.map(id => ({ id: getFormattedId(id) }));
    let activitiesWithStudentInfo;

    try {
      activitiesWithStudentInfo = await getActivitiesWithStudentInfo(formattedEntityIdList, 20, "partner", "scored");
    } catch (error) {
      console.error("Error fetching activities with student info:", error);
      return [];
    }

    if (activitiesWithStudentInfo && Array.isArray(activitiesWithStudentInfo)) {
      const initialStudents = formattedEntityIdList.reduce((acc, { id }) => {
        acc[id] = { entityId: id, assignments: [] };
        return acc;
      }, {});

      studentAssignments = Object.values(activitiesWithStudentInfo.reduce((acc, obj) => {
        if (!acc[obj.studentId]) {
          acc[obj.studentId] = { entityId: obj.student.id, assignments: [] };
        }
        acc[obj.studentId].assignments.push({
          assignmentId: obj.activityId,
          entityType: "STUDENT",
          entityId: obj.student.id,
          status: "DONE",
          createdAt: obj.createdAt,
          assignmentType: "BENCHMARK",
          manifest: [{ activityType: 'assessment', activity: [{activityId: obj.activityId, createdAt: obj.createdAt, status: obj.status, displayStatus: obj.displayStatus, type: "assessment"}], locale: "EN_US", sequence: "BREAK", status: "COMPLETE" }]
        });
        return acc;
      }, initialStudents));
    } else {
      console.error("activitiesWithStudentInfo is not an array or is undefined.");
      return [];
    }
  }

  const startDate = new Date(dateStart).setHours(0, 0, 0, 0);
  const endDate = new Date(dateEnd).setHours(23, 59, 59, 999);

  studentAssignments.forEach(student => {
    if (student.assignments && Array.isArray(student.assignments)) {
      student.assignments = student.assignments.filter(assignment => {
        let updatedAt = getCompletedDate(assignment);
        if (updatedAt) {
          updatedAt = new Date(formatDate(updatedAt)).setHours(0, 0, 0, 0);
        } else {
          updatedAt = new Date(assignment.createdAt).setHours(0, 0, 0, 0);
        }
        return !statusesToFilter.includes(assignment.status) || (updatedAt >= startDate && updatedAt <= endDate);
      });
    } else {
      console.error("student.assignments is not an array or is undefined for student:", student);
    }
  });
  return studentAssignments;
}

function isActiveDistrictAssignment(assignment) {
  const {entityType, dateFrom, dateTo} = assignment
  // entityType must be DISTRICT or SCHOOL
  if ([ENTITY_TYPE.DISTRICT, ENTITY_TYPE.SCHOOL].includes(entityType)) {
    // window must be active (dateFrom < now < dateTo)
    const start = new Date(dateFrom)
    const end = new Date(dateTo)
    const now = new Date()
    if (now > start && now < end) return true
  }
  return false
}

function getMostRecentAssignment(assignments) {
  let mostRecentAssignment = null
  let maxDate = null
  for (const assignment of assignments) {
    const {status, createdAt, entityType} = assignment
    // skip district assignments
    if ([ENTITY_TYPE.DISTRICT,ENTITY_TYPE.SCHOOL].includes(entityType)) continue
    // skip unassigned assignments
    if (status === 'UNASSIGNED') continue
    const date = new Date(createdAt)
    if (!maxDate || date > maxDate) {
      mostRecentAssignment = assignment
      maxDate = date
    }
  }
  return mostRecentAssignment
}

function getCompletedDate(assignment) {
  if (!assignment) return null
  const {status, manifest} = assignment
  if (['ASSIGNED', 'IN_PROGRESS'].includes(status)) return null
  let latestDate = null
  for (const m of manifest) {
    const activities = m.activity
    for (const act of activities) {
      const date = new Date(act.createdAt)
      if (!latestDate || date > latestDate) latestDate = date;
    }
  }
  return latestDate ? latestDate.toString() : null
}

function getCreationDate(assignment) {
  if (!assignment) return null
  let str = assignment.createdAt // YYYY-MM-DD HH:mm:ss date string in UTC
  str = str.split(' ').join('T')
  str += '.000Z'
  return new Date(str).toString()
}

// convert javascript date to YYYY-MM-DD
function formatDate(d = new Date()) {
  return moment(d).format('YYYY-MM-DD')
}

export default {
  getReport,
}